import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * Handles the streaming chat protocol (SSE-over-fetch) with /assistant/chat.
 * Keeps the message list, session id, and busy flag in one place so the
 * page component stays purely presentational.
 */
export function useAssistantChat({ contextAdId }) {
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);

  const send = useCallback(
    async (raw) => {
      const content = (raw || "").trim();
      if (!content || busy) return;
      setBusy(true);
      const userMsgId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const assistantMsgId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: "user", content },
        { id: assistantMsgId, role: "assistant", content: "" },
      ]);

      try {
        const res = await fetch(`${API}/assistant/chat`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionRef.current,
            message: content,
            context_ad_id: contextAdId || null,
          }),
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const blocks = buf.split("\n\n");
          buf = blocks.pop() || "";
          for (const block of blocks) {
            if (!block.startsWith("data:")) continue;
            try {
              const payload = JSON.parse(block.slice(5).trim());
              if (payload.type === "session") {
                sessionRef.current = payload.session_id;
              } else if (payload.type === "delta") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, content: (m.content || "") + payload.content }
                      : m,
                  ),
                );
              } else if (payload.type === "error") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsgId
                      ? { ...m, content: `⚠ Assistant error: ${payload.message}` }
                      : m,
                  ),
                );
              }
            } catch (err) {
              // Skip malformed SSE chunk quietly.
            }
          }
        }
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: `⚠ Could not reach the assistant: ${err.message}` }
              : m,
          ),
        );
      } finally {
        setBusy(false);
      }
    },
    [busy, contextAdId],
  );

  return { messages, busy, send };
}

/** Auto-scroll a ref-attached container to the bottom whenever `deps` change. */
export function useAutoScroll(deps) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}
